import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useMyBenefitApplicationsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

// Cancelable only before any approver has acted (backend enforces the rest).
const CANCELABLE_STATUSES = ['submitted', 'waiting'];

export default function MyBenefitApplicationsPage() {
  const { t } = useTranslation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [cancelConfirm, setCancelConfirm] = useState({ isOpen: false, id: null });
  const [cancelling, setCancelling] = useState(false);

  const {
    objectSchema, fetching, columns: baseColumns, data, pagination, create, cancel,
  } = useMyBenefitApplicationsApi(queryParams);

  const handleNewButtonClick = useCallback(() => setDrawerOpen(true), []);

  const handleSave = useCallback(async (formData) => {
    const success = await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [create]);

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelConfirm.id) return;
    setCancelling(true);
    await cancel(cancelConfirm.id);
    setCancelConfirm({ isOpen: false, id: null });
    setCancelling(false);
  }, [cancelConfirm.id, cancel]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const buildActions = useCallback((row) => {
    if (!row || !CANCELABLE_STATUSES.includes(row.status)) return [];
    return [{
      type: 'cancel',
      label: t('benefitApplication.cancelAction'),
      onClick: () => setCancelConfirm({ isOpen: true, id: row.id }),
    }];
  }, [t]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const actions = buildActions(row);
          return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('benefitApplication.newApplication')}
        schema={objectSchema}
        initialValues={null}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={cancelConfirm.isOpen}
        title={t('benefitApplication.cancelAction')}
        message={t('benefitApplication.cancelConfirm')}
        onConfirm={handleCancelConfirm}
        onCancel={() => setCancelConfirm({ isOpen: false, id: null })}
        loading={cancelling}
      />

      {useMemo(() => (
        <DataTable
          title={t('benefitApplication.myTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination,
        handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
